/* src/components/journal/TagInput.js */
import React from 'react';

/**
 * Controlled Input component for entering and displaying tags.
 * Uses HTML <datalist> for suggestions.
 */
const TagInput = ({
  tags = [],
  availableTags = [],
  onChange,
  inputValue = '',
  onInputChange,
  disabled = false
}) => {
  // Handles key presses within the input field (Enter or Comma to add tag)
  const handleKeyDown = (e) => {
    if (disabled) return;
    
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault(); // Prevent default form submission or comma insertion
      addCurrentTag();
    }
  };

  // Adds the current input value as a tag if valid
  const addCurrentTag = () => {
    const newTagValue = inputValue.trim();
    
    // Validate tag format (limit length, ensure # prefix)
    let formattedTag = newTagValue;
    if (formattedTag && !formattedTag.startsWith('#')) {
      formattedTag = `#${formattedTag}`;
    }
    
    // Check length limit (25 chars is a reasonable max)
    if (formattedTag.length > 25) {
      formattedTag = formattedTag.substring(0, 25);
    }
    
    // Only add if valid and not a duplicate
    if (formattedTag && !tags.includes(formattedTag)) {
      onChange([...tags, formattedTag]);
    }
    
    // Clear the input field
    if (onInputChange) {
      onInputChange('');
    }
  };

  // Removes a tag from the confirmed list when its 'x' button is clicked
  const removeTag = (tagToRemove) => {
    if (disabled) return;
    onChange(tags.filter(tag => tag !== tagToRemove));
  };

  // Handles changes to the text input field
  const handleInputChange = (e) => {
    if (onInputChange) {
      onInputChange(e.target.value);
    }
  };

  // When input field loses focus, add current tag if valid
  const handleBlur = () => {
    if (inputValue.trim()) {
      addCurrentTag();
    }
  };

  return (
    <div className={`rounded-lg border ${disabled ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'} p-3 transition-colors`}>
      {/* Container for displaying confirmed tags */}
      <div className="flex flex-wrap gap-2 mb-2 min-h-[28px]">
        {tags.map((tag, index) => (
          <span
            key={index}
            className={`bg-indigo-100 text-indigo-700 text-xs font-medium px-2.5 py-1.5 rounded-full 
                      flex items-center group transition-colors ${disabled ? 'opacity-70' : 'hover:bg-indigo-200'}`}
          >
            {tag}
            {/* Remove tag button */}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-1.5 text-indigo-500 group-hover:text-indigo-700 font-bold text-xs focus:outline-none focus:text-indigo-800"
                aria-label={`Remove ${tag}`}
                disabled={disabled}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>

      {/* Text input field for adding new tags */}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={disabled ? "Tags disabled" : "Add tags (press Enter or comma)..."}
          disabled={disabled}
          className={`w-full py-1.5 px-3 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500 ${
            disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-gray-50 text-gray-700 hover:bg-white focus:bg-white'
          } transition-colors`}
          list="available-tags"
          aria-label="Add tags"
        />
        {inputValue && !disabled && (
          <button 
            type="button"
            onClick={addCurrentTag}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-indigo-500 hover:text-indigo-700 p-1"
            aria-label="Add tag"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {/* Datalist for tag suggestions */}
      <datalist id="available-tags">
        {availableTags
          .filter(at => !tags.some(t => t.toLowerCase() === at.toLowerCase()) && 
                        !(at.toLowerCase() === inputValue.toLowerCase()))
          .map(tag => (
            <option key={tag} value={tag} />
          ))}
      </datalist>
    </div>
  );
};

export default TagInput;